import { useState } from "react";
import { ArrowLeft, Edit, Trash2, Plus, Zap } from "lucide-react";
import { Card } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/card";
import { Button } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/button";
import { useNavigate, useParams } from "react-router-dom";
import { Progress } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/progress";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/alert-dialog";
import { useToast } from "@/projects/Admin_Bite_Balance/bite-balance/src/hooks/use-toast";

const MealDetail = () => {
  const navigate = useNavigate();
  const { mealType } = useParams();
  const { toast } = useToast();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<number | null>(null);

  const getGlucoseSpikeLevel = (
    avgGL: number
  ): { level: string; color: string; bgColor: string; description: string } => {
    if (avgGL >= 15) {
      return {
        level: "High",
        color: "text-destructive",
        bgColor: "bg-destructive/10 border-destructive/30",
        description:
          "This meal may cause a significant blood sugar spike. Consider adding fiber or protein to slow absorption.",
      };
    }
    if (avgGL >= 8) {
      return {
        level: "Medium",
        color: "text-amber-500",
        bgColor: "bg-amber-500/10 border-amber-500/30",
        description:
          "Moderate glucose impact expected. The meal has a balanced glycemic profile.",
      };
    }
    return {
      level: "Low",
      color: "text-emerald-500",
      bgColor: "bg-emerald-500/10 border-emerald-500/30",
      description:
        "Minimal blood sugar impact expected. Great choice for stable energy levels.",
    };
  };

  const mealData: Record<string, any> = {
    breakfast: {
      name: "Breakfast",
      time: "8:30 AM",
      totalCalories: 420,
      mealScore: 82,
      protein: 18,
      carbs: 65,
      fat: 12,
      glucoseSpike: "Medium",
      image:
        "https://images.unsplash.com/photo-1533089860892-a7c6f0a88666?w=800",
      items: [
        {
          name: "Oatmeal with berries",
          portion: "1 cup (250g)",
          calories: 210,
          protein: 8,
          carbs: 42,
          fiber: 7,
          fat: 4,
          sugar: 12,
          gi: 55,
          gl: 23,
        },
        {
          name: "Greek yogurt",
          portion: "150g",
          calories: 150,
          protein: 15,
          carbs: 12,
          fiber: 2,
          fat: 5,
          sugar: 8,
          gi: 11,
          gl: 1,
        },
        {
          name: "Green tea",
          portion: "1 cup (240ml)",
          calories: 2,
          protein: 0,
          carbs: 0,
          fiber: 0,
          fat: 0,
          sugar: 0,
          gi: 0,
          gl: 0,
        },
      ],
    },
    lunch: {
      name: "Lunch",
      time: "1:00 PM",
      totalCalories: 580,
      mealScore: 75,
      protein: 38,
      carbs: 45,
      fat: 22,
      glucoseSpike: "Low",
      image: "https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=800",
      items: [
        {
          name: "Grilled chicken salad",
          portion: "200g",
          calories: 280,
          protein: 32,
          carbs: 8,
          fiber: 3,
          fat: 14,
          sugar: 3,
          gi: 15,
          gl: 1,
        },
        {
          name: "Quinoa",
          portion: "1 cup (185g)",
          calories: 220,
          protein: 8,
          carbs: 39,
          fiber: 5,
          fat: 4,
          sugar: 2,
          gi: 53,
          gl: 18,
        },
        {
          name: "Olive oil dressing",
          portion: "2 tbsp (30ml)",
          calories: 240,
          protein: 0,
          carbs: 0,
          fiber: 0,
          fat: 28,
          sugar: 0,
          gi: 0,
          gl: 0,
        },
      ],
    },
    snack: {
      name: "Snack",
      time: "4:30 PM",
      totalCalories: 220,
      mealScore: 68,
      protein: 7,
      carbs: 28,
      fat: 11,
      glucoseSpike: "Low",
      image:
        "https://images.unsplash.com/photo-1619566636858-adf3ef46400b?w=800",
      items: [
        {
          name: "Apple with almond butter",
          portion: "1 medium apple + 2 tbsp",
          calories: 180,
          protein: 4,
          carbs: 25,
          fiber: 5,
          fat: 8,
          sugar: 18,
          gi: 38,
          gl: 9,
        },
        {
          name: "Mixed nuts",
          portion: "30g",
          calories: 170,
          protein: 6,
          carbs: 6,
          fiber: 3,
          fat: 15,
          sugar: 2,
          gi: 15,
          gl: 1,
        },
      ],
    },
    dinner: {
      name: "Dinner",
      time: "7:30 PM",
      totalCalories: 520,
      mealScore: 88,
      protein: 35,
      carbs: 42,
      fat: 18,
      glucoseSpike: "High",
      image:
        "https://images.unsplash.com/photo-1467003909585-2f8a72700288?w=800",
      items: [
        {
          name: "Baked salmon",
          portion: "150g",
          calories: 280,
          protein: 25,
          carbs: 0,
          fiber: 0,
          fat: 18,
          sugar: 0,
          gi: 0,
          gl: 0,
        },
        {
          name: "Sweet potato",
          portion: "1 medium (150g)",
          calories: 130,
          protein: 2,
          carbs: 30,
          fiber: 4,
          fat: 0,
          sugar: 7,
          gi: 63,
          gl: 17,
        },
        {
          name: "Steamed broccoli",
          portion: "1 cup (150g)",
          calories: 55,
          protein: 4,
          carbs: 11,
          fiber: 4,
          fat: 0,
          sugar: 2,
          gi: 10,
          gl: 1,
        },
      ],
    },
  };

  const meal = mealData[mealType || "breakfast"];

  // If meal type is invalid, redirect to meals page
  if (!meal) {
    navigate("/meals");
    return null;
  }

  const handleDeleteItem = () => {
    if (itemToDelete !== null) {
      toast({
        title: "Food item deleted",
        description: "The food item has been removed from this meal.",
      });
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  };

  const handleEditItem = (item: any, index: number) => {
    navigate(`/meals/edit-food-item`, {
      state: {
        item: {
          ...item,
          servingSize: item.portion,
        },
        mealType,
        itemIndex: index,
      },
    });
  };

  const handleAddItem = () => {
    navigate(`/meals/add-food-item`, { state: { mealType } });
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="mx-auto max-w-md">
        {/* Header with Image */}
        <div className="relative h-56">
          <img
            src={meal.image}
            alt={meal.name}
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/meals")}
            className="absolute left-4 top-4 rounded-xl bg-background/80 backdrop-blur-sm"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="absolute bottom-4 left-4 right-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-foreground">
                  {meal.name}
                </h1>
                <p className="text-sm text-muted-foreground">{meal.time}</p>
              </div>
              <div className="text-center">
                <div
                  className={`text-2xl font-bold ${meal.mealScore >= 80 ? "text-emerald-500" : meal.mealScore >= 60 ? "text-amber-500" : "text-red-500"}`}
                >
                  {meal.mealScore}
                </div>
                <p className="text-xs text-muted-foreground">Meal Score</p>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-4 p-4">
          {/* Glucose Spike Predictor */}
          {(() => {
            const spikeData =
              meal.glucoseSpike === "High"
                ? {
                    level: "High",
                    color: "text-destructive",
                    bgColor: "bg-destructive/10 border border-destructive/30",
                    description:
                      "This meal may cause a significant blood sugar spike. Consider adding fiber or protein to slow absorption.",
                  }
                : meal.glucoseSpike === "Medium"
                  ? {
                      level: "Medium",
                      color: "text-amber-500",
                      bgColor: "bg-amber-500/10 border border-amber-500/30",
                      description:
                        "Moderate glucose impact expected. The meal has a balanced glycemic profile.",
                    }
                  : {
                      level: "Low",
                      color: "text-emerald-500",
                      bgColor: "bg-emerald-500/10 border border-emerald-500/30",
                      description:
                        "Minimal blood sugar impact expected. Great choice for stable energy levels.",
                    };
            return (
              <Card
                className={`rounded-2xl border-0 p-4 shadow-sm ${spikeData.bgColor}`}
              >
                <div className="mb-2 flex items-center gap-3">
                  <div
                    className={`flex h-9 w-9 items-center justify-center rounded-lg ${
                      spikeData.level === "High"
                        ? "bg-destructive/20"
                        : spikeData.level === "Medium"
                          ? "bg-amber-500/20"
                          : "bg-emerald-500/20"
                    }`}
                  >
                    <Zap className={`h-5 w-5 ${spikeData.color}`} />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground">
                      Predicted Glucose Spike
                    </p>
                    <p className={`text-lg font-bold ${spikeData.color}`}>
                      {spikeData.level}
                    </p>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  {spikeData.description}
                </p>
              </Card>
            );
          })()}

          {/* Macros Card */}
          <Card className="rounded-2xl border-0 p-5 shadow-sm">
            <h3 className="mb-4 font-semibold text-foreground">
              Nutritional Breakdown
            </h3>
            <div className="space-y-4">
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-sm text-foreground">Protein</span>
                  <span className="text-chart-1 text-sm font-medium">
                    {meal.protein}g
                  </span>
                </div>
                <Progress value={(meal.protein / 100) * 100} className="h-2" />
              </div>
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-sm text-foreground">Carbohydrates</span>
                  <span className="text-chart-2 text-sm font-medium">
                    {meal.carbs}g
                  </span>
                </div>
                <Progress value={(meal.carbs / 200) * 100} className="h-2" />
              </div>
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-sm text-foreground">Fats</span>
                  <span className="text-chart-3 text-sm font-medium">
                    {meal.fat}g
                  </span>
                </div>
                <Progress value={(meal.fat / 65) * 100} className="h-2" />
              </div>
            </div>
          </Card>

          {/* Food Items */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-foreground">Food Items</h3>
              <Button
                size="sm"
                variant="ghost"
                className="h-8"
                onClick={handleAddItem}
              >
                <Plus className="mr-1 h-4 w-4" />
                Add Item
              </Button>
            </div>

            {meal.items.map((item: any, index: number) => (
              <Card
                key={index}
                className="rounded-xl border-0 p-4 shadow-sm transition-shadow hover:shadow-md"
              >
                <div className="mb-3 flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="mb-1 font-medium text-foreground">
                      {item.name}
                    </h4>
                    <p className="text-xs text-muted-foreground">
                      {item.portion}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 w-8 p-0"
                      onClick={() => handleEditItem(item, index)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 w-8 p-0 text-destructive"
                      onClick={() => {
                        setItemToDelete(index);
                        setDeleteDialogOpen(true);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-2 text-xs">
                  <div className="text-center">
                    <div className="font-bold text-foreground">
                      {item.calories}
                    </div>
                    <div className="text-[10px] text-muted-foreground">
                      kcal
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="font-bold text-foreground">
                      {item.protein}g
                    </div>
                    <div className="text-[10px] text-muted-foreground">
                      Protein
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="font-bold text-foreground">{item.fat}g</div>
                    <div className="text-[10px] text-muted-foreground">Fat</div>
                  </div>
                  <div className="text-center">
                    <div className="font-bold text-foreground">
                      {item.carbs}g
                    </div>
                    <div className="text-[10px] text-muted-foreground">
                      Total Carbs
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="font-bold text-foreground">
                      {item.fiber}g
                    </div>
                    <div className="text-[10px] text-muted-foreground">
                      Fiber
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="font-bold text-foreground">
                      {item.sugar}g
                    </div>
                    <div className="text-[10px] text-muted-foreground">
                      Sugar
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="font-bold text-foreground">{item.gi}</div>
                    <div className="text-[10px] text-muted-foreground">GI</div>
                  </div>
                  <div className="text-center">
                    <div className="font-bold text-foreground">{item.gl}</div>
                    <div className="text-[10px] text-muted-foreground">GL</div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Food Item</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this food item? This action cannot
              be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteItem}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default MealDetail;
